// src/components/offers/NegotiationModal.jsx
import { X } from 'lucide-react';
import { useState } from 'react';
import FormInput from '../forms/FormInput';
import Button from '../layout/ui/Button';
import LoadingSpinner from '../common/LoadingSpinner';
import api from '../../lib/api';
import { success, error } from '../common/Toast';
import { formatMoney } from '../../lib/helpers';

export default function NegotiationModal({ offer, isOpen, onClose, onSuccess }) {
  const [amount, setAmount] = useState('');
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);

  if (!isOpen || !offer) return null;

  const handleSubmit = async () => {
    if (!amount || Number(amount) <= 0) {
      error('Please enter a valid amount');
      return;
    }

    setLoading(true);
    try {
      await api.post(`/offers/${offer.id}/negotiate`, {
        proposed_amount: Number(amount),
        proposed_terms: comment.trim() || 'Counter offer',
      });

      success(`Counter offer of ${formatMoney(amount)} sent!`);
      onSuccess?.();
      onClose();
    } catch (err) {
      error(err.response?.data?.message || 'Failed to send counter offer');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl shadow-2xl max-w-lg w-full"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-8 border-b">
          <div>
            <h2 className="text-3xl font-bold">Negotiate Offer</h2>
            <p className="text-gray-600 mt-2">
              Current: <span className="font-bold text-primary">{formatMoney(offer.amount)}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-3 hover:bg-gray-100 rounded-xl transition"
          >
            <X size={28} />
          </button>
        </div>

        {/* Body */}
        <div className="p-8 space-y-7">
          <FormInput
            label="Your Counter Offer Amount (₦)"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder={offer.amount}
            required
            className="text-2xl"
          />

          <div>
            <label className="block font-medium text-gray-700 mb-3">
              Message to Parent <span className="text-gray-400">(optional)</span>
            </label>
            <textarea
              rows={4}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="e.g. Can we do ₦95,000 with Sundays off and feeding included?"
              className="w-full px-5 py-4 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-primary focus:border-primary outline-none resize-none text-gray-700"
            />
          </div>

          <div className="flex gap-4">
            <Button variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              isLoading={loading}
              disabled={loading || !amount}
              className="flex-1 bg-primary hover:bg-primary/90 text-white font-bold text-lg py-4 rounded-2xl"
            >
              {loading ? (
                <>
                  <LoadingSpinner size="sm" color="white" className="mr-3" />
                  Sending...
                </>
              ) : (
                'Send Counter Offer'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}